import { tx } from "@/i18n/tx";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  tx("ui_aichatdrawer.k286935"),
  tx("ui_aichatdrawer.k93acce"),
  tx("ui_aichatdrawer.k96c713"),
  tx("ui_aichatdrawer.kf1af48"),
];

export default function AiChatDrawer() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: tx("ui_aichatdrawer.kdd969a") },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open) api.get("/ai/usage").then((r) => setQuota(r.data)).catch(() => {});
  }, [open]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    if (quota && !quota.allowed) {
      toast.error(tx("ui_aichatdrawer.k399f69"));
      return;
    }
    const history = messages.slice(-6);
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q, history }, { timeout: 45000 });
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "…" }]);
      if (data.quota) setQuota(data.quota);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 429) setQuota((prev) => ({ ...(prev || {}), allowed: false }));
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${msg}` }]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } };

  const quotaLabel = (() => {
    if (!quota) return null;
    if (quota.limit < 0) return tx("ui_aichatdrawer.kbb2b81");
    return `${quota.remaining} message${quota.remaining > 1 ? "s" : ""} restant${quota.remaining > 1 ? "s" : ""} ce mois`;
  })();

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ai-chat-fab"
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-secondary-c text-white shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        style={{ boxShadow: "0 10px 30px -8px rgba(200,160,64,0.5)" }}
        aria-label={tx("ui_aichatdrawer.k39fd4b")}
      >
        <Sparkles className="w-6 h-6" fill="currentColor" fillOpacity={0.15} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setOpen(false)}>
          <div
            data-testid="ai-chat-drawer"
            onClick={(e) => e.stopPropagation()}
            className="bg-surface w-full sm:max-w-md h-[80vh] sm:h-[600px] rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden page-enter"
          >
            <header className="px-5 py-4 border-b border-c flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-2xl bg-secondary-c flex items-center justify-center text-white">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-display text-lg leading-none">Oresto AI</p>
                  <p className="text-[11px] text-text-secondary mt-0.5" data-testid="ai-quota-label">
                    {quotaLabel || tx("r3.assistant")}
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-text-secondary hover:text-[var(--text)] p-1">
                <X className="w-5 h-5" />
              </button>
            </header>

            {quota && !quota.allowed && (
              <div className="mx-4 mt-3 rounded-2xl border border-red-300 bg-red-50 p-3" data-testid="ai-quota-blocked">
                <p className="text-sm font-semibold text-red-700">{tx("ui_aichatdrawer.k97431b")}</p>
                <p className="text-xs text-red-600 mt-1">Vous avez utilisé vos {quota.limit} messages du mois. Passez à la formule annuelle pour un usage illimité.</p>
                <Link to="/app/plus" onClick={() => setOpen(false)} className="inline-block mt-2 text-xs font-semibold text-red-700 underline">
                  {tx("ui_aichatdrawer.kfdaf5e")}
                </Link>
              </div>
            )}

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary-c text-white rounded-br-md"
                        : "bg-surface-muted text-[var(--text)] rounded-bl-md"
                    }`}
                    data-testid={`ai-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-surface-muted rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-c" />
                    <span className="text-xs text-text-secondary">{tx("ui_aichatdrawer.k11cbe3")}</span>
                  </div>
                </div>
              )}
              {messages.length === 1 && (
                <div className="pt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary mb-2">{tx("ui_aichatdrawer.k41ecca")}</p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => ask(s)}
                        data-testid={`ai-suggestion-${s.slice(0, 20)}`}
                        className="text-left text-sm bg-surface-muted hover:bg-[#ece8da] rounded-xl px-3 py-2.5 border border-c transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-c bg-surface shrink-0">
              <div className="flex gap-2">
                <textarea
                  data-testid="ai-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  disabled={loading}
                  rows={1}
                  placeholder={tx("ui_aichatdrawer.kc6c480")}
                  className="input-c text-sm resize-none flex-1"
                  style={{ minHeight: "44px", maxHeight: "120px" }}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  data-testid="ai-chat-send"
                  className="w-11 h-11 rounded-2xl bg-primary-c text-white flex items-center justify-center disabled:opacity-40 shrink-0"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-text-secondary mt-2 text-center">{tx("ui_aichatdrawer.k9b74df")}</p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
